import React, { useState } from 'react';
import { Box, IconButton, Link, Typography } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import AuthAlert from '../components/auth/AuthAlert';
import AuthField from '../components/auth/AuthField';
import AuthPrimaryButton from '../components/auth/AuthPrimaryButton';
import AuthScreenShell from '../components/auth/AuthScreenShell';
import AuthStepTransition from '../components/auth/AuthStepTransition';
import LoginTermsHint, { PasswordHint } from '../components/auth/LoginTermsHint';
import SocialLoginSection from '../components/auth/SocialLoginSection';
import { authScreen } from '../components/auth/authScreenStyles';
import { useT } from '../i18n/useT';
import { checkUserEmailExists, registerUser } from '../services/api';
import { getApiLanguage } from '../config/apiLanguage';

type AuthStep = 'email' | 'password' | 'register';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REGISTER_PASSWORD_REGEX =
  /(?=.*[a-z])(?=.*[A-Z])(?=.*[\\'\/~`!@#$%^&*()_\-+={}\[\]|;:"<>,.?0-9]).{8,40}$/;
const BIRTHDAY_REGEX = /^(0[1-9]|[1-2][0-9]|3[0-1])\.(0[1-9]|1[0-2])\.[0-9]{4}$/;

const authInputSx = {
  width: '100%',
  border: 'none',
  outline: 'none',
  bgcolor: 'transparent',
  color: authScreen.text,
  fontSize: '0.9375rem',
  fontFamily: authScreen.font,
  fontWeight: 500,
  letterSpacing: '-0.01em',
  '&::placeholder': { color: authScreen.textDim },
};

function formatBirthdayInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

function EmailFieldWithEdit({
  email,
  onEdit,
  disabled,
}: {
  email: string;
  onEdit: () => void;
  disabled?: boolean;
}) {
  const t = useT();
  return (
    <AuthField label={t('loginEmailLabel')}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
        <Typography
          sx={{
            flex: 1,
            color: authScreen.textMuted,
            fontSize: '0.9375rem',
            wordBreak: 'break-all',
            fontFamily: authScreen.font,
            fontWeight: 500,
          }}
        >
          {email}
        </Typography>
        <Link
          component="button"
          type="button"
          disabled={disabled}
          onClick={onEdit}
          underline="hover"
          sx={{
            color: authScreen.accent,
            fontSize: '0.8125rem',
            flexShrink: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
            border: 'none',
            bgcolor: 'transparent',
            fontFamily: authScreen.font,
            fontWeight: 500,
          }}
        >
          {t('loginEditEmail')}
        </Link>
      </Box>
    </AuthField>
  );
}

function PasswordField({
  value,
  onChange,
  disabled,
  autoFocus,
  helperText,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  helperText?: React.ReactNode;
}) {
  const t = useT();
  const [show, setShow] = useState(false);

  return (
    <AuthField label={t('password')} helperText={helperText}>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Box
          component="input"
          autoFocus={autoFocus}
          disabled={disabled}
          type={show ? 'text' : 'password'}
          autoComplete="current-password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          sx={authInputSx}
        />
        <IconButton
          aria-label={show ? t('loginHidePassword') : t('loginShowPassword')}
          onClick={() => setShow((v) => !v)}
          disabled={disabled}
          sx={{ color: authScreen.textMuted, ml: 0.5, '&:hover': { color: authScreen.text } }}
          size="small"
        >
          {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </Box>
    </AuthField>
  );
}

export default function LoginPage() {
  const t = useT();
  const [step, setStep] = useState<AuthStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login, establishSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = location.state?.message;

  const resetToEmailStep = () => {
    setStep('email');
    setPassword('');
    setFirstName('');
    setLastName('');
    setBirthday('');
    setError(null);
  };

  const validateEmail = (value: string): boolean => {
    if (!value || !EMAIL_REGEX.test(value)) {
      setError(t('loginInvalidEmail'));
      return false;
    }
    return true;
  };

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!validateEmail(trimmed)) return;

    setIsLoading(true);
    try {
      const { exists } = await checkUserEmailExists(trimmed, getApiLanguage());
      setEmail(trimmed);
      setStep(exists ? 'password' : 'register');
    } catch {
      setError(t('loginEmailCheckFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!password) {
      setError(t('loginFillAllFields'));
      return;
    }
    setIsLoading(true);
    try {
      await login(email, password);
      navigate('/homepage');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!firstName.trim() || !password) {
      setError(t('loginFillAllFields'));
      return;
    }
    if (!REGISTER_PASSWORD_REGEX.test(password)) {
      setError(t('registerPasswordRules'));
      return;
    }
    const birthdayValue = birthday.trim();
    if (!BIRTHDAY_REGEX.test(birthdayValue)) {
      setError(t('registerBirthdayInvalid'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await registerUser(
        email,
        password,
        firstName.trim(),
        birthdayValue,
        1,
        getApiLanguage(),
        lastName.trim() || undefined
      );

      if (response.title && response.message && !response.token) {
        throw new Error(response.message);
      }

      const token = response.token;
      const userId = response.user?.id;
      if (token && userId) {
        establishSession({
          id: userId,
          token,
          email: response.user?.email || email,
          firstName: response.user?.firstName || firstName,
          lastName: response.user?.lastName || lastName,
          verifiedEmail: false,
        });
        navigate('/homepage');
        return;
      }

      if (response.message) {
        navigate('/homepage');
        return;
      }

      throw new Error(t('registerFailed'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('registerFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const heading =
    step === 'email' ? t('loginOrRegisterTitle') : step === 'password' ? t('loginSignInTitle') : t('registerTitle');

  return (
    <AuthScreenShell
      title={heading}
      subtitle={step === 'email' ? t('loginSubtitle') : undefined}
      stepKey={step}
    >
      {successMessage ? <AuthAlert message={successMessage} tone="success" /> : null}
      {error ? <AuthAlert message={error} tone="error" /> : null}

      {step === 'email' && (
        <AuthStepTransition stepKey="email">
          <form onSubmit={handleEmailContinue}>
            <AuthField label={t('loginEmailLabel')}>
              <Box
                component="input"
                type="email"
                autoFocus
                required
                autoComplete="email"
                disabled={isLoading}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={authInputSx}
              />
            </AuthField>
            <AuthPrimaryButton loading={isLoading}>{t('loginContinue')}</AuthPrimaryButton>
          </form>
          <LoginTermsHint />
          <SocialLoginSection
            disabled={isLoading}
            onSuccess={() => navigate('/homepage')}
            onError={(message) => setError(message)}
          />
        </AuthStepTransition>
      )}

      {step === 'password' && (
        <AuthStepTransition stepKey="password">
          <form onSubmit={handlePasswordLogin}>
            <EmailFieldWithEdit email={email} onEdit={resetToEmailStep} disabled={isLoading} />
            <PasswordField value={password} onChange={setPassword} disabled={isLoading} autoFocus />
            <AuthPrimaryButton loading={isLoading}>{t('loginSignIn')}</AuthPrimaryButton>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                component={RouterLink}
                to="/?forgot=1"
                underline="hover"
                sx={{ color: authScreen.textMuted, fontSize: '0.8125rem', fontFamily: authScreen.font, fontWeight: 500 }}
              >
                {t('loginForgotPassword')}
              </Link>
            </Box>
          </form>
        </AuthStepTransition>
      )}

      {step === 'register' && (
        <AuthStepTransition stepKey="register">
          <form onSubmit={handleRegister}>
            <EmailFieldWithEdit email={email} onEdit={resetToEmailStep} disabled={isLoading} />
            <PasswordField
              value={password}
              onChange={setPassword}
              disabled={isLoading}
              autoFocus
              helperText={<PasswordHint text={t('registerPasswordHint')} />}
            />
            <AuthField label={t('registerFirstName')}>
              <Box
                component="input"
                required
                autoComplete="given-name"
                disabled={isLoading}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                sx={authInputSx}
              />
            </AuthField>
            <AuthField label={t('registerLastName')}>
              <Box
                component="input"
                autoComplete="family-name"
                disabled={isLoading}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                sx={authInputSx}
              />
            </AuthField>
            <AuthField label={t('registerBirthday')} helperText={t('registerBirthdayHint')}>
              <Box
                component="input"
                required
                inputMode="numeric"
                disabled={isLoading}
                value={birthday}
                placeholder={t('registerBirthdayPlaceholder')}
                onChange={(e) => setBirthday(formatBirthdayInput(e.target.value))}
                sx={authInputSx}
              />
            </AuthField>
            <LoginTermsHint compact />
            <AuthPrimaryButton loading={isLoading}>{t('registerSubmit')}</AuthPrimaryButton>
          </form>
        </AuthStepTransition>
      )}
    </AuthScreenShell>
  );
}
